import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $accountListStore, $initialCurrenciesAvailableStore, $initialAccountsStore } from '@lib/store';
import { useTabs, useView, ViewEnum } from '@shared/hooks';
import { cn, PaletteUtils } from '@shared/utils';
import { AccountStatus, SortType } from '@shared/api';
import { AccountsGrid, AccountsTable } from './widgets';
import { Cards, PageContainer, List as Transactions, View } from '@widgets';
import { Button, Icon, Select, Tabs, Text } from '@shared/widgets';
import GridViewIcon from '@assets/icons/grid-view.svg';
import LineViewIcon from '@assets/icons/line-view.svg';

export const AccountsPage = () => {
  const accountDefaultListStore = useUnit($initialAccountsStore.store);
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const accountsStore = useUnit($accountListStore.store);

  const [status, setStatus] = useState<AccountStatus | undefined>(AccountStatus.ACTIVE);
  const [order, setOrder] = useState<SortType>(SortType.Desc);
  const [currency, setCurrency] = useState<string | undefined>();

  const tabsState = useTabs();
  const viewState = useView({ page: 'accounts' });
  const { t } = useTranslation('accounts');

  useEffect(() => {
    if (!accountDefaultListStore.data.length) return;
    const hasActiveAccounts = accountDefaultListStore.data.some((account) => account.status === AccountStatus.ACTIVE);
    if (status === AccountStatus.ACTIVE && !hasActiveAccounts) setStatus(undefined);
  }, [accountDefaultListStore.data, status]);

  useEffect(() => {
    $accountListStore.events.getAccounts({ Status: status, SortType: order, Currency: currency });
    return () => $accountListStore.events.clear();
  }, [status, order, currency]);

  const orderOptions = useMemo(
    () => [
      { label: t('order.asc', { ns: 'common' }), value: SortType.Asc },
      { label: t('order.desc', { ns: 'common' }), value: SortType.Desc },
    ],
    [t],
  );
  const statusOptions = useMemo(() => {
    const statuses = [...new Set(accountDefaultListStore.data.map((account) => account.status))];
    return statuses.map((status) => ({ label: t(`statuses.account.${status}`, { ns: 'common' }), value: status }));
  }, [accountDefaultListStore.data, t]);
  const accountIds = useMemo(
    () => (accountDefaultListStore.data || []).filter((item) => item.type === tabsState.tab).map((item) => item.id),
    [tabsState.tab, accountDefaultListStore.data],
  );
  const cardAccountsIds = useMemo(
    () => accountsStore.data.filter((account) => account.type === tabsState.tab).map((account) => account.id),
    [accountsStore.data, tabsState.tab],
  );
  return (
    <PageContainer
      title={t('title')}
      header={
        <div className='flex'>
          <Text>{t('header')}</Text>
          {PaletteUtils.isInsigniaPalette && !accountsStore.loading && (
            <span
              className={cn(
                'ml-2 flex h-8 w-8 justify-center rounded-2xl px-2 text-white dark:bg-primary',
                PaletteUtils.isInsigniaPalette && 'bg-soft-black',
                PaletteUtils.isVcfcPalette && 'bg-primary',
              )}
            >
              <span className='opacity-1 transition ease-in'>{accountsStore.data.length || 0}</span>
            </span>
          )}
        </div>
      }
      topRightAddons={
        <>
          <Button
            className={cn('h-10 w-10 rounded-sm', PaletteUtils.isVcfcPalette && 'bg-light/10', 'hover:bg-light/60 dark:hover:bg-light/30')}
            color='icon'
            onClick={() => viewState.onChangeView(ViewEnum.Grid)}
          >
            <Icon
              className={cn(
                PaletteUtils.isInsigniaPalette && [viewState.isGrid && '!text-primary'],
                PaletteUtils.isVcfcPalette && ['dark:!text-white/50', viewState.isGrid && '!text-primary dark:!text-white'],
              )}
              icon={<GridViewIcon />}
            />
          </Button>
          <Button
            className={cn('ml-4 h-10 w-10 rounded-sm', PaletteUtils.isVcfcPalette && 'bg-light/10', 'hover:bg-light/60 dark:hover:bg-light/30')}
            color='icon'
            onClick={() => viewState.onChangeView(ViewEnum.List)}
          >
            <Icon
              className={cn(
                PaletteUtils.isInsigniaPalette && [viewState.isList && '!text-primary'],
                PaletteUtils.isVcfcPalette && ['dark:!text-white/50', viewState.isList && '!text-primary dark:!text-white'],
              )}
              icon={<LineViewIcon />}
            />
          </Button>
        </>
      }
    >
      {PaletteUtils.isVcfcPalette && tabsState.hasCard && tabsState.hasPayment && (
        <Tabs options={tabsState.tabs} value={tabsState.tab!} onChange={tabsState.onChangeTab} />
      )}
      <div
        className={cn(
          PaletteUtils.isVcfcPalette &&
            viewState.isList &&
            'mb-6 mt-6 rounded-xl bg-white p-6 dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black',
        )}
      >
        <div className={cn('flex flex-row', 'max-[900px]:flex-col max-[900px]:gap-2', PaletteUtils.isVcfcPalette && viewState.isGrid && 'mt-10')}>
          {PaletteUtils.isInsigniaPalette && tabsState.hasCard && tabsState.hasPayment && (
            <Tabs options={tabsState.tabs} value={tabsState.tab!} onChange={tabsState.onChangeTab} />
          )}
          <div
            className={cn(
              'ml-auto flex flex-row space-x-4',
              'max-[900px]:ml-0',
              PaletteUtils.isVcfcPalette && 'max-md:flex-col max-md:gap-2 max-md:space-x-0',
            )}
          >
            <Select
              className={cn('min-w-[140px]', PaletteUtils.isVcfcPalette && 'max-md:w-full')}
              items={statusOptions}
              nullValue={{ isEnable: true, text: t('placeholders.allStatuses', { ns: 'common' }) }}
              value={status}
              onChange={setStatus}
            />
            <Select className='min-w-[190px]' items={orderOptions} value={order} onChange={(val) => setOrder(val!)} />
            <Select
              className={cn('min-w-[160px]', PaletteUtils.isVcfcPalette && 'max-md:w-full')}
              items={currenciesAvailableStore.data}
              nullValue={{ isEnable: true, text: t('placeholders.allCurrencies', { ns: 'common' }) }}
              value={currency}
              onChange={(val) => setCurrency(val!)}
            />
          </div>
        </div>
        <div className={cn('my-12', PaletteUtils.isVcfcPalette && 'my-4')}>
          <View
            loading={accountsStore.loading}
            error={accountsStore.error}
            onRefresh={() => $accountListStore.events.getAccounts({ Status: status, SortType: order, Currency: currency })}
          >
            {viewState.isGrid && <AccountsGrid accountType={tabsState.tab!} accounts={accountsStore.data} currency={currency} />}
            {viewState.isList && <AccountsTable accountType={tabsState.tab!} accounts={accountsStore.data} />}
          </View>
        </div>
      </div>
      <div
        className={cn(PaletteUtils.isVcfcPalette && 'mb-6 rounded-xl bg-white dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black')}
      >
        <Cards title={t('card.title')} listType={viewState.isList} accountIds={cardAccountsIds} currency={currency} />
      </div>
      <div
        className={cn(
          'flex w-full flex-col gap-6',
          PaletteUtils.isVcfcPalette && 'rounded-xl bg-white p-6 dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black',
        )}
      >
        {PaletteUtils.isInsigniaPalette && (
          <Text weight='semibold' size={PaletteUtils.isVcfcPalette ? 'lg' : '2xl'}>
            {t('transactions.title')}
          </Text>
        )}
        <Transactions accountType={tabsState.tab!} accountIds={accountIds} search filter />
      </div>
    </PageContainer>
  );
};
